<div class="page-container">
  <app-card>
    <app-card-content>
      <!-- Organization Settings Section -->
      <div class="settings-section">
        <h3>{{ 'settings.organization.title' | translate }}</h3>

        <div class="setting-item">
          <div class="setting-header flex gap-md flex-grow" style="width:100%">
            <div>
              <h4>{{ 'settings.organization.nameLabel' | translate }}</h4>
              <p class="setting-value">{{ organizationName() || ('settings.organization.noName' | translate) }}</p>
            </div>
            @if (userRole() === 'owner') {
            <app-button variant="outlined" (click)="showRenameOrganization()" [disabled]="!canUseNetwork()"
              data-testid="rename-organization-button">
              <app-icon name="edit-2" />
              <span>{{ 'settings.organization.rename' | translate }}</span>
            </app-button>
            }
          </div>
          @if (!canUseNetwork()) {
          <p class="setting-description muted">
            {{ 'settings.organization.renameOnlineOnly' | translate }}
          </p>
          }
        </div>
      </div>

      <!-- Billing Section -->
      <div class="settings-section" *hasPermission="'org.billing'">
        <h3>{{ 'settings.billing.title' | translate }}</h3>

        <div class="setting-item">
          <div class="setting-header flex gap-md flex-grow" style="width:100%">
            <div>
              <p class="setting-description">{{ 'settings.billing.description' | translate }}</p>
            </div>
            <app-button variant="outlined" routerLink="/settings/billing" data-testid="go-to-billing-button">
              <app-icon name="credit-card" />
              <span>{{ 'settings.billing.manage' | translate }}</span>
            </app-button>
          </div>
        </div>
      </div>

      <!-- Network Settings Section -->
      <div class="settings-section">
        <h3>{{ 'settings.connection.title' | translate }}</h3>

        <div class="setting-item">
          <div class="setting-header">
            <h4>{{ 'settings.stayOffline.title' | translate }}</h4>
            <app-checkbox [(ngModel)]="stayOfflineModeValue" (ngModelChange)="toggleStayOfflineMode()"
              data-testid="stay-offline-toggle" />
          </div>
          <p class="setting-description">
            {{ 'settings.stayOffline.description' | translate }}
          </p>
        </div>

        @if (stayOfflineMode() && isOnline()) {
        <div class="manual-sync-section">
          <app-button variant="outlined" (click)="triggerManualSync()" [disabled]="syncingManually() || syncSuccess()"
            data-testid="manual-sync-button">
            @if (syncSuccess()) {
            <app-icon name="check-circle" />
            <span>{{ 'settings.manualSync.success' | translate }}</span>
            } @else {
            <app-icon name="refresh-cw" [class.spinning]="syncingManually()" />
            @if (syncingManually()) {
            <span>{{ 'settings.manualSync.updating' | translate }}</span>
            } @else {
            <span>{{ 'settings.manualSync.button' | translate }}</span>
            }
            }
          </app-button>

          @if (syncError()) {
          <div class="error-message">
            <div class="error-content">
              <app-icon name="alert-circle" />
              <span>{{ syncError() }}</span>
            </div>
            <button class="dismiss-button" (click)="dismissSyncError()"
              [attr.aria-label]="'settings.manualSync.dismiss' | translate">
              <app-icon name="x" />
            </button>
          </div>
          }

          @if (syncSuccess()) {
          <div class="success-message">
            <app-icon name="check-circle" />
            <span>{{ 'settings.sync.successMessage' | translate }}</span>
          </div>
          }
        </div>
        }

        <div class="status-chips">
          <app-chip [color]="isOnline() ? 'success' : 'danger'">
            {{ 'settings.status.network' | translate:{ status: (isOnline() ? ('settings.status.networkOnline' |
            translate) : ('settings.status.networkOffline' | translate)) } }}
          </app-chip>
          <app-chip [color]="syncConnected() ? 'success' : 'primary'">
            {{ 'settings.status.server' | translate:{ status: (syncConnected() ? ('settings.status.serverConnected' |
            translate) : ('settings.status.serverDisconnected' | translate)) } }}
          </app-chip>
        </div>
      </div>

      <!-- Scanner Settings Section -->
      <div class="settings-section">
        <h3>{{ 'settings.scanner.title' | translate }}</h3>

        <div class="setting-item">
          <div class="setting-info">
            <h4>{{ 'settings.scanner.cameraTimeout.title' | translate }}</h4>
            <p class="setting-description">
              {{ 'settings.scanner.cameraTimeout.description' | translate }}
            </p>
          </div>
          <div class="timeout-input-group">
            <app-input>
              <input type="number" [(ngModel)]="cameraTimeoutValue" (ngModelChange)="onCameraTimeoutChange($event)"
                min="5" max="180" step="5" data-testid="camera-timeout-input" />
            </app-input>
            <span class="timeout-unit">{{ 'settings.scanner.cameraTimeout.seconds' | translate }}</span>
          </div>
        </div>
      </div>

      <!-- Demo Mode Section -->
      @if (canOverrideArtwork) {
      <div class="settings-section">
        <h3>{{ 'settings.demoMode.title' | translate }}</h3>
        <p class="section-description muted">
          {{ 'settings.demoMode.description' | translate }}
        </p>

        <div class="setting-item">
          <div class="setting-info">
            <h4>{{ 'settings.demoMode.autoReset.title' | translate }}</h4>
            <p class="setting-description">
              {{ 'settings.demoMode.autoReset.description' | translate }}
            </p>
          </div>
          <input type="number" class="form-control" [(ngModel)]="expiryMinutes"
            [min]="environmentService.getMinExpiryMinutes()" [max]="environmentService.getMaxExpiryMinutes()"
            [step]="15" data-testid="expiry-minutes-input" />
        </div>

        <div class="setting-item">
          <div class="setting-info">
            <h4>{{ 'settings.demoMode.artwork.title' | translate }}</h4>
            <p class="setting-description">
              {{ 'settings.demoMode.artwork.description' | translate:{ currentEnvironment: currentEnvironment } }}
              @if (overrideExpiresAt) {
              <span class="expiry-notice">
                {{ 'settings.demoMode.artwork.overrideExpiry' | translate:{ expiresAt: ((overrideExpiresAt |
                date:'short') ?? '') } }}
              </span>
              }
            </p>
          </div>
          <app-select data-testid="artwork-env-select">
            <select [(ngModel)]="selectedArtworkEnv" (ngModelChange)="onArtworkEnvChange($event)" class="form-control">
              @for (option of artworkEnvironmentOptions; track option.value) {
              <option [value]="option.value">{{ option.label }}</option>
              }
            </select>
          </app-select>
        </div>
      </div>
      }

      <!-- Feature Flags Section -->
      <div class="settings-section">
        <h3>{{ 'settings.featureFlags.title' | translate }}</h3>
        <p class="section-description muted">{{ 'settings.featureFlags.comingSoon' | translate }}</p>
      </div>

      <!-- Super Admin Section -->
      @if (isSuperAdmin()) {
        <div class="settings-section">
          <h3>Admin</h3>
          <div class="setting-item">
            <div class="setting-header flex gap-md flex-grow" style="width:100%">
              <div>
                <p class="setting-description">Cross-organization QA tools (super admin only).</p>
              </div>
              <app-button variant="outlined" routerLink="/admin/subscriptions" data-testid="go-to-admin-subscriptions-button">
                <app-icon name="shield" />
                <span>Subscriptions</span>
              </app-button>
            </div>
          </div>
        </div>
      }

    </app-card-content>
  </app-card>
</div>
<!-- Rename Organization Modal -->
@if (showRenameDialog()) {
<app-modal-dialog (backdropClick)="cancelRename()">
  <div header>
    <div class="modal-title-row">
      <div class="heading-md">{{ 'settings.organization.renameDialog.title' | translate }}</div>
    </div>
  </div>

  <div content>
    <p class="modal-description">
      {{ 'settings.organization.renameDialog.description' | translate }}
    </p>

    <app-form-field [label]="'settings.organization.renameDialog.nameLabel' | translate">
      <app-input>
        <input type="text" [(ngModel)]="newOrganizationName"
          [placeholder]="'settings.organization.renameDialog.namePlaceholder' | translate"
          data-testid="new-organization-name-input" (keyup.enter)="confirmRename()" required />
      </app-input>
    </app-form-field>
  </div>

  <div footer>
    <app-button variant="text" (click)="cancelRename()" [disabled]="renamingOrganization()"
      data-testid="cancel-rename-button">
      {{ 'settings.organization.renameDialog.cancel' | translate }}
    </app-button>
    <app-button variant="filled" color="primary" (click)="confirmRename()"
      [disabled]="renamingOrganization() || !newOrganizationName().trim() || !organizationNameChanged()"
      data-testid="confirm-rename-button">
      @if (renamingOrganization()) {
      <span>{{ 'settings.organization.renameDialog.saving' | translate }}</span>
      } @else {
      <span>{{ 'settings.organization.renameDialog.save' | translate }}</span>
      }
    </app-button>
  </div>
</app-modal-dialog>
}